/**
 * What happens next, said once, immediately after somebody is added.
 *
 * Adding a member sends two emails from two different systems, and neither is
 * sent by RogerEMR: the portal invitation comes from WorkOS and the app setup
 * directions come from Roger-Docs. The operator has no way to see either, so
 * without this they hand over a name and hope. The support questions that
 * follow are always the same ones -- which email, from whom, why has it not
 * arrived yet, and which app do I download -- so they are answered here rather
 * than left to be asked.
 *
 * The literal sender and subject text matters more than it looks: an operator
 * who knows what to look for can find the mail in a spam folder, and one who
 * does not will report it missing.
 */

import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@roger/ui-components';
import { Clock, Loader2, Mail, Smartphone } from 'lucide-react';
import { inferDeployStageLabel } from '../../../utils/deployStage';

/**
 * The two emails, as they actually arrive.
 *
 * Neither is sent from this codebase, so these are transcribed from the real
 * messages rather than derived, and will drift if either sender changes their
 * template. The portal subject carries an environment prefix outside production
 * (`[STAGING] …`), which is deliberately not reproduced here.
 */
const PORTAL_EMAIL = {
  subject: 'You’ve been invited to Roger Healthcare',
  expiryDays: 7,
} as const;

/**
 * Who the portal invitation comes from, which is not one address.
 *
 * When the server reports that Roger sent it (the roger-sends-invite-emails
 * flag, read for the whole WorkOS environment), it came through SendGrid from
 * the support address. Otherwise WorkOS sends it, and each WorkOS
 * environment has its own verified sending domain: production sends from the
 * Roger domain, and every other environment falls back to the WorkOS default.
 * Naming the wrong one is worse than naming none, because this is the address
 * the operator tells somebody to search their spam folder for.
 */
function portalSender(rogerSendsInviteEmails: boolean): string {
  if (rogerSendsInviteEmails) {
    return 'Roger Healthcare <support@rogerhealthcare.com>';
  }
  // Lowercased before comparing, as the other caller of this does: the label is
  // the `VITE_DEPLOY_STAGE` value verbatim when that is set, so a stage spelled
  // `Prod` would otherwise read as somewhere else entirely.
  return inferDeployStageLabel().toLowerCase() === 'prod'
    ? 'Roger Healthcare <no-reply@mail.rogerhealthcare.com>'
    : 'Roger Healthcare <welcome@workos.dev>';
}

const APP_EMAIL = {
  sender: 'support@rogerhealthcare.com',
  subject: 'Setup Directions for Roger AI Charting App',
} as const;

const APP_STORE_LINKS = {
  ios: 'https://apps.apple.com/us/app/roger-home-health-assistant/id6462757466',
  android: 'https://play.google.com/store/apps/details?id=com.yuseflo.frontend',
} as const;

export interface OnboardedConfirmationProps {
  open: boolean;
  /** Their name, or their email address when we were not given one. */
  name: string;
  /** Where the portal invitation was sent. */
  email: string;
  /** Their Roger App username, when an account was created or linked. */
  appUsername?: string | null;
  /** Whether a Roger App account was part of this. */
  hasAppAccount: boolean;
  /**
   * Whether an invitation email was actually sent. False when the person
   * already had a Roger login and was simply added to the agency -- they are in,
   * but nothing landed in their inbox.
   */
  portalInvited: boolean;
  /** Whether Roger sent the invitation through SendGrid instead of WorkOS. */
  rogerSendsInviteEmails: boolean;
  /**
   * Whether the app account was created rather than an existing one linked.
   * Only a new account gets setup directions and a password.
   */
  appAccountCreated: boolean;
  /**
   * Whether RogerDoc reported actually sending those setup directions.
   * `appAccountCreated` only says an account was made, so alone it promises an
   * email nobody checked was sent. `null` means RogerDoc did not say, which an
   * older deploy does, and is treated as sent.
   */
  appSetupEmailSent?: boolean | null;
  onClose: () => void;
  /** Resends the portal invitation, reporting whether it worked. Roger App has
   * no equivalent. */
  onResendInvite: () => Promise<boolean>;
}

/**
 * How many emails to expect, which is not the same as how many things happened.
 *
 * Somebody who already had a Roger login is added to the agency without an
 * invitation, and a linked app account sends no setup directions. Both still
 * count as success; neither puts anything in an inbox.
 */
function describeEmails({
  portalInvited,
  hasAppAccount,
  appAccountCreated,
  appEmailSent,
}: {
  portalInvited: boolean;
  hasAppAccount: boolean;
  appAccountCreated: boolean;
  appEmailSent: boolean;
}): string {
  const emails = (portalInvited ? 1 : 0) + (appEmailSent ? 1 : 0);
  if (emails === 2) {
    return 'They will get two emails — one for the portal, one for the Roger App.';
  }
  // Said before anything else when it happened: the operator is about to hand
  // over a name, and this is the part that changes what they say.
  if (appAccountCreated && !appEmailSent) {
    return portalInvited
      ? 'Their Roger App setup email could not be sent — they will only get the portal email.'
      : 'Their Roger App setup email could not be sent.';
  }
  if (portalInvited) {
    return 'They will get an email to set up their portal login.';
  }
  if (appEmailSent) {
    return 'They will get an email with their Roger App setup directions.';
  }
  return hasAppAccount
    ? 'They already had both logins, so no email was sent — they can use the ones they have.'
    : 'They already had a Roger login, so no invitation was sent — they can sign in with it.';
}

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className='flex flex-col gap-0.5 sm:flex-row sm:gap-2'>
    <span className='shrink-0 text-xs text-muted-foreground sm:w-16'>
      {label}
    </span>
    <span className='break-words text-xs font-medium'>{value}</span>
  </div>
);

export const OnboardedConfirmation: React.FC<OnboardedConfirmationProps> = ({
  open,
  name,
  email,
  appUsername,
  hasAppAccount,
  portalInvited,
  rogerSendsInviteEmails,
  appAccountCreated,
  appSetupEmailSent,
  onClose,
  onResendInvite,
}) => {
  const [isResending, setIsResending] = useState(false);
  const [resent, setResent] = useState(false);
  // Only an explicit false is a failure: null or undefined is a RogerDoc that
  // did not report, and there is no reason to alarm the operator over that.
  const appEmailSent = appAccountCreated && appSetupEmailSent !== false;

  const handleResend = async () => {
    setIsResending(true);
    try {
      // Only on success. Marking it sent regardless left the button saying
      // "Invitation resent" and disabled after a failure, so the one person who
      // needed to try again could not.
      if (await onResendInvite()) setResent(true);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={value => !value && onClose()}>
      <DialogContent className='max-h-[90vh] w-full max-w-lg overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>{name} was added</DialogTitle>
          <DialogDescription>
            {describeEmails({
              portalInvited,
              hasAppAccount,
              appAccountCreated,
              appEmailSent,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className='flex flex-col gap-4'>
          {/* ── Portal login ── */}
          <div className='rounded-md border p-3'>
            <div className='flex items-center gap-2'>
              <Mail className='h-4 w-4 text-muted-foreground' />
              <h4 className='text-sm font-semibold'>Portal login</h4>
            </div>
            {portalInvited ? (
              <>
                <div className='mt-2 flex flex-col gap-1'>
                  <Row label='To' value={email} />
                  <Row
                    label='From'
                    value={portalSender(rogerSendsInviteEmails)}
                  />
                  <Row label='Subject' value={PORTAL_EMAIL.subject} />
                </div>
                <p className='mt-2 text-xs text-muted-foreground'>
                  The link expires in {PORTAL_EMAIL.expiryDays} days.
                </p>
              </>
            ) : (
              <p className='mt-2 text-xs text-muted-foreground'>
                {email} already had a Roger login, so no invitation was sent.
                They can sign in with the password they already use — this
                agency is now one of the ones they can open.
              </p>
            )}
            {portalInvited && (
              <div className='mt-2 flex items-center gap-2'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={handleResend}
                  disabled={isResending || resent}
                >
                  {isResending && (
                    <Loader2 className='mr-2 h-3.5 w-3.5 animate-spin' />
                  )}
                  {resent ? 'Invitation resent' : 'Resend invitation'}
                </Button>
                {resent && (
                  <span className='text-xs text-muted-foreground'>
                    Give it another few minutes.
                  </span>
                )}
              </div>
            )}
          </div>

          {/* ── Roger App ── */}
          {hasAppAccount && (
            <div className='rounded-md border p-3'>
              <div className='flex items-center gap-2'>
                <Smartphone className='h-4 w-4 text-muted-foreground' />
                <h4 className='text-sm font-semibold'>Roger App</h4>
              </div>
              <div className='mt-2 flex flex-col gap-1'>
                {appEmailSent && (
                  <>
                    <Row label='From' value={APP_EMAIL.sender} />
                    <Row label='Subject' value={APP_EMAIL.subject} />
                  </>
                )}
                {appUsername && <Row label='Username' value={appUsername} />}
              </div>
              {appAccountCreated && !appEmailSent ? (
                // No From/Subject above: there is no message to go looking for,
                // and naming one is what sends people through their spam folder.
                <p className='mt-2 text-xs font-medium text-destructive'>
                  Their account exists, but the setup email could not be sent,
                  so they have no password yet. Have them use “Forgot Password”
                  in the Roger App with this username to set one.
                </p>
              ) : (
                <p className='mt-2 text-xs text-muted-foreground'>
                  {appEmailSent ? (
                    <>
                      That email carries their app password, which is separate
                      from their portal login. It cannot be resent from here —
                      ask {APP_EMAIL.sender} if it does not arrive.
                    </>
                  ) : (
                    <>
                      An account they already had was linked, so no setup email
                      was sent and their app password is unchanged.
                    </>
                  )}
                </p>
              )}
              <p className='mt-2 text-xs'>
                <span className='text-muted-foreground'>Download: </span>
                <a
                  href={APP_STORE_LINKS.ios}
                  target='_blank'
                  rel='noreferrer'
                  className='font-medium underline'
                >
                  iPhone
                </a>
                <span className='text-muted-foreground'> · </span>
                <a
                  href={APP_STORE_LINKS.android}
                  target='_blank'
                  rel='noreferrer'
                  className='font-medium underline'
                >
                  Android
                </a>
              </p>
            </div>
          )}

          <p className='flex items-start gap-2 text-xs text-muted-foreground'>
            <Clock className='mt-0.5 h-3.5 w-3.5 shrink-0' />
            <span>
              Email can take 3 to 5 minutes to arrive. If it has not shown up,
              ask them to check their spam or junk folder before resending —
              that is where it usually is.
            </span>
          </p>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardedConfirmation;
